'use client';

import { c as _c } from "react/compiler-runtime";
import { Content as HoverCardContentPrimitive, Portal as HoverCardPortal, Root as HoverCardRoot, Trigger as HoverCardTriggerPrimitive } from '@radix-ui/react-hover-card';
import type { ComponentProps, ComponentPropsWithoutRef, ComponentRef, Ref } from 'react';
import { createContext, useContext } from 'react';
import { cn } from '@/lib/utils';
const HoverCardNestingContext = createContext(false);
type HoverCardProperties = ComponentProps<typeof HoverCardRoot> & {
  allowNested?: boolean;
};
function HoverCard(t0) {
  const $ = _c(9);
  let children;
  let properties;
  let t1;
  if ($[0] !== t0) {
    ({
      allowNested: t1,
      children,
      ...properties
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = properties;
    $[3] = t1;
  } else {
    children = $[1];
    properties = $[2];
    t1 = $[3];
  }
  const allowNested = t1 === undefined ? false : t1;
  const isNested = useContext(HoverCardNestingContext);
  if (isNested && !allowNested) {
    let t2;
    if ($[4] !== children) {
      t2 = <>{children}</>;
      $[4] = children;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    return t2;
  }
  let t2;
  if ($[6] !== children || $[7] !== properties) {
    t2 = <HoverCardNestingContext.Provider value={true}><HoverCardRoot {...properties}>{children}</HoverCardRoot></HoverCardNestingContext.Provider>;
    $[6] = children;
    $[7] = properties;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}
const HoverCardTrigger = HoverCardTriggerPrimitive;
type HoverCardContentProperties = ComponentPropsWithoutRef<typeof HoverCardContentPrimitive> & {
  ref?: Ref<ComponentRef<typeof HoverCardContentPrimitive>>;
};
const HoverCardContent = t0 => {
  const $ = _c(14);
  let className;
  let properties;
  let ref;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      className,
      align: t1,
      sideOffset: t2,
      ref,
      ...properties
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = properties;
    $[3] = ref;
    $[4] = t1;
    $[5] = t2;
  } else {
    className = $[1];
    properties = $[2];
    ref = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  const align = t1 === undefined ? "center" : t1;
  const sideOffset = t2 === undefined ? 4 : t2;
  let t3;
  if ($[6] !== className) {
    t3 = cn("z-layer-popover w-64 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none will-change-[transform,opacity] contain-layout data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-hover-card-content-transform-origin]", className);
    $[6] = className;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== align || $[9] !== properties || $[10] !== ref || $[11] !== sideOffset || $[12] !== t3) {
    t4 = <HoverCardPortal><HoverCardContentPrimitive ref={ref} align={align} sideOffset={sideOffset} className={t3} {...properties} /></HoverCardPortal>;
    $[8] = align;
    $[9] = properties;
    $[10] = ref;
    $[11] = sideOffset;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
};
export { HoverCard, HoverCardTrigger, HoverCardContent };